import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroCar from "@/assets/hero-car.jpg";

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroCar}
          alt={t("hero.title")}
          width="1920"
          height="1080"
          className="w-full h-full object-cover"
          fetchpriority="high"
          loading="eager"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Animated gold particles (Snow effect) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none w-full h-full">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-primary/40"
            initial={{
              left: `${Math.random() * 100}%`,
              top: -20,
              opacity: 0,
            }}
            animate={{
              top: "110%",
              opacity: [0, 0.8, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "linear",
            }}
            style={{
              filter: "blur(0.5px)",
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="inline-block gold-gradient text-primary-foreground px-6 py-2 rounded-full text-sm font-semibold mb-6 tracking-wider">
            {t("hero.badge")}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-shadow-gold text-foreground"
        >
          {t("hero.title")}
          <br />
          <span className="gold-text">{t("hero.subtitle")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-lg md:text-xl text-foreground/60 mb-10 max-w-2xl mx-auto"
        >
          {t("hero.description")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href="#booking"
            className="gold-gradient px-10 py-4 rounded-full text-primary-foreground font-bold text-lg hover:opacity-90 transition-all animate-pulse-gold"
          >
            {t("hero.bookNow")}
          </a>
          <a
            href="#gallery"
            className="border border-primary/30 px-10 py-4 rounded-full text-foreground font-semibold text-lg hover:border-primary/60 hover:bg-primary/5 transition-all"
          >
            {t("hero.viewGallery")}
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="text-primary/60" size={32} />
      </motion.div>
    </section>
  );
};

export default HeroSection;
